import { useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { InputNumber, Select, Spin } from 'antd';
import { StageTaskType } from '@/store/Lifecycle/constants';
import { useStores } from '@/hooks/use-stores';
import { useTask } from './Provider';
import { getTaskFieldMetadata } from './constants';
import { useStage } from '../Stage/Provider';

interface Unit {
  amount: number;
  type: string;
}

export const UnitsInput = observer(() => {
  const task = useTask<StageTaskType.Units>();
  const { name, value, isSaving, options, isReadOnly, optionMapper } = getTaskFieldMetadata(task);
  const [isLoading, setIsLoading] = useState(false);
  const { lifecycleStore } = useStores();
  const { id: stageId } = useStage();
  const [amount, setAmount] = useState(value?.amount);
  const [type, setType] = useState(value?.type);

  const validateValuesNotEmpty = useCallback(({ amount, type }: Unit) => {
    return amount !== undefined && type !== undefined;
  }, []);

  const saveTask = useCallback(
    async (updatedValue: Unit) => {
      if (validateValuesNotEmpty(updatedValue)) {
        await lifecycleStore.updateTask(stageId, name, updatedValue);
      }
    },
    [lifecycleStore, name, stageId, validateValuesNotEmpty],
  );

  const onAmountChange = (newAmount: Unit['amount']) => {
    setAmount(newAmount);
  };

  const onAmountBlur = () => {
    const updatedValue = { amount, type };
    saveTask(updatedValue);
  };

  const onTypeChange = (newType: Unit['type']) => {
    setType(newType);
    const updatedValue = { amount, type: newType };
    saveTask(updatedValue);
  };

  const unitsOptions = (options || [{ value: type }])?.map(
    optionMapper ||
      (option => ({
        label: option.name,
        value: option.value,
      })),
  );

  return (
    <InputNumber
      className="stage-task-input"
      size="large"
      disabled={isReadOnly || isSaving}
      min={0}
      value={amount}
      onChange={onAmountChange}
      onBlur={onAmountBlur}
      addonAfter={
        <Select
          value={type}
          style={{ width: 100 }}
          disabled={isReadOnly || isSaving}
          onChange={onTypeChange}
          options={isLoading ? [] : unitsOptions}
          loading={isSaving}
          notFoundContent={isLoading ? <Spin size="small" /> : null}
          onDropdownVisibleChange={async isVisible => {
            if (isVisible && !options) {
              try {
                setIsLoading(true);
                await lifecycleStore.fetchTaskOptions(name);
              } finally {
                setIsLoading(false);
              }
            }
          }}
        />
      }
    />
  );
});
